import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { getServiceBySlug, SERVICES } from "../lib/services";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { getServiceSeo, serviceJsonLd, breadcrumbJsonLd } from "../lib/seo";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);
  if (!service) return <Navigate to="/services" replace />;

  const Icon = service.icon;
  const idx = SERVICES.findIndex((s) => s.slug === slug);
  const next = SERVICES[(idx + 1) % SERVICES.length];
  const prev = SERVICES[(idx - 1 + SERVICES.length) % SERVICES.length];
  const seo = getServiceSeo(service);

  return (
    <div className="pt-36 pb-8" data-testid="service-detail-page">
      <Seo
        path={`/services/${service.slug}`}
        title={seo.title}
        description={seo.description}
        jsonLd={[
          serviceJsonLd(service),
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Services", path: "/services" },
            { name: service.title, path: `/services/${service.slug}` },
          ]),
        ]}
      />
      <section className="max-w-7xl mx-auto px-6 lg:px-10">
        <Link to="/services" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-[#d4a840] transition-colors" data-testid="service-back-link">
          <ArrowLeft size={16} /> All services
        </Link>

        <div className="mt-8 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="flex items-center gap-4 mb-6">
                <div className="h-14 w-14 rounded-xl bg-[#d4a840]/15 border border-[#d4a840]/40 flex items-center justify-center">
                  <Icon size={26} className="text-[#d4a840]" />
                </div>
                <div className="kb-label">Service · {String(idx + 1).padStart(2, "0")}</div>
              </div>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-black text-white leading-[1.0]" data-testid="service-title">
                {service.title}
              </h1>
              <p className="mt-6 text-lg text-white/70 max-w-2xl leading-relaxed">{service.tagline}</p>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="mt-12 kb-glass rounded-2xl p-8">
                <div className="kb-label mb-4">What we handle</div>
                <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                  {service.items.map((it) => (
                    <li key={it} className="flex items-start gap-3 text-white/85" data-testid={`service-item-${it}`}>
                      <Check size={18} className="text-[#d4a840] mt-0.5 shrink-0" />
                      <span className="text-sm leading-relaxed">{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/contact" className="kb-btn-primary" data-testid="service-cta-primary">
                  Enquire about this service <ArrowRight size={18} />
                </Link>
                <a href="tel:+918867192221" className="kb-btn-ghost" data-testid="service-cta-call">
                  Call +91 88671 92221
                </a>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <Reveal delay={0.1}>
              <div className="kb-glass rounded-2xl p-8">
                <div className="kb-label mb-4">Our promise</div>
                <ul className="space-y-4 text-white/80 text-sm">
                  <li className="flex gap-3"><Check size={18} className="text-[#d4a840] mt-0.5 shrink-0" /> Dedicated engagement manager</li>
                  <li className="flex gap-3"><Check size={18} className="text-[#d4a840] mt-0.5 shrink-0" /> Fixed-fee, transparent pricing</li>
                  <li className="flex gap-3"><Check size={18} className="text-[#d4a840] mt-0.5 shrink-0" /> Compliance calendar with reminders</li>
                  <li className="flex gap-3"><Check size={18} className="text-[#d4a840] mt-0.5 shrink-0" /> Response within 24 hours</li>
                </ul>
                <div className="mt-6 pt-6 border-t border-[#d4a840]/15 text-xs text-white/60 leading-relaxed">
                  Every engagement includes an initial diagnostic call at no cost. We only
                  recommend what your business actually needs.
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Prev / next */}
        <div className="mt-20 grid md:grid-cols-2 gap-5">
          <Link to={`/services/${prev.slug}`} className="kb-glass rounded-2xl p-6 group" data-testid="service-prev">
            <div className="kb-label mb-2">Previous</div>
            <div className="flex items-center gap-3 text-white/85 font-display text-lg">
              <ArrowLeft size={18} className="text-[#d4a840] group-hover:-translate-x-1 transition-transform" /> {prev.title}
            </div>
          </Link>
          <Link to={`/services/${next.slug}`} className="kb-glass rounded-2xl p-6 group text-right" data-testid="service-next">
            <div className="kb-label mb-2">Next</div>
            <div className="flex items-center justify-end gap-3 text-white/85 font-display text-lg">
              {next.title} <ArrowRight size={18} className="text-[#d4a840] group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>
        </div>
      </section>
    </div>
  );
}
